import React, { useEffect, useMemo, useState } from 'react';
import Badge from 'shared/components/UC/Badge';
import Button from 'shared/components/UC/Button';
import Checkbox from 'shared/components/UC/Checkbox';
import Pagination from 'shared/components/Pagination';
import Switch from 'shared/components/UC/Switch';
import {
  EvaluatorLoadRow,
  ImportIssueCode,
  ImportPreview,
  ImportPreviewRow,
} from '../../../types/bulkAssignment';
import { IMPORT_ISSUE_CATALOG } from './importIssues';
import MatchConfidenceBadge from './MatchConfidenceBadge';
import styles from './ImportPreviewTable.module.css';

export type PreviewView = 'application' | 'evaluator';

type ResolvedEvaluator = EvaluatorLoadRow & {
  projectedLoad: number;
  overQuota: boolean;
};

interface ImportPreviewTableProps {
  preview: ImportPreview;
  discarded: Set<string>;
  evaluators: ResolvedEvaluator[];
  view: PreviewView;
  onViewChange: (view: PreviewView) => void;
  issueFilter: ImportIssueCode | null;
  onIssueFilterChange: (code: ImportIssueCode | null) => void;
  onToggleMatch: (key: string) => void;
  onToggleRow: (folio: string) => void;
}

interface DerivedRow {
  row: ImportPreviewRow;
  kept: number;
  finalCount: number;
  codes: Set<ImportIssueCode>;
  assignable: boolean;
  importableMatches: number;
}

const PAGE_SIZES = [25, 50, 100];

const ImportPreviewTable: React.FC<ImportPreviewTableProps> = ({
  preview,
  discarded,
  evaluators,
  view,
  onViewChange,
  issueFilter,
  onIssueFilterChange,
  onToggleMatch,
  onToggleRow,
}) => {
  const [search, setSearch] = useState('');
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZES[0]);

  const overQuotaKeys = useMemo(
    () => new Set(evaluators.filter((e) => e.overQuota).map((e) => e.key)),
    [evaluators]
  );

  const derivedRows = useMemo<DerivedRow[]>(
    () =>
      preview.rows.map((row) => {
        const codes = new Set<ImportIssueCode>(row.issues);
        let kept = 0;
        let importableMatches = 0;
        row.matches.forEach((match) => {
          if (match.importable) importableMatches += 1;
          if (discarded.has(match.key)) return;
          match.issues.forEach((code) => codes.add(code));
          if (match.importable) kept += 1;
          const evaluatorKey =
            match.evaluatorId ?? `unknown:${match.evaluatorEmail.toLowerCase()}`;
          if (match.importable && overQuotaKeys.has(evaluatorKey)) {
            codes.add('OVER_QUOTA');
          }
        });

        const assignable =
          !row.issues.includes('UNKNOWN_FOLIO') && !row.issues.includes('WRONG_STATUS');
        const finalCount = assignable ? row.existingActiveEvaluators + kept : 0;

        if (assignable && !row.issues.includes('APPLICATION_MISSING_FROM_FILE')) {
          if (finalCount === 0) codes.add('NO_COVERAGE');
          else if (finalCount < preview.minEvaluatorsPerApplication) {
            codes.add('PARTIAL_COVERAGE');
          }
        }

        return { row, kept, finalCount, codes, assignable, importableMatches };
      }),
    [preview.rows, preview.minEvaluatorsPerApplication, discarded, overQuotaKeys]
  );

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return derivedRows.filter((item) => {
      if (issueFilter && !item.codes.has(issueFilter)) return false;
      if (onlyIssues && item.codes.size === 0) return false;
      if (!q) return true;
      return (
        item.row.folio.toLowerCase().includes(q) ||
        item.row.title.toLowerCase().includes(q) ||
        item.row.irName.toLowerCase().includes(q) ||
        item.row.matches.some(
          (m) =>
            m.evaluatorName.toLowerCase().includes(q) ||
            m.evaluatorEmail.toLowerCase().includes(q)
        )
      );
    });
  }, [derivedRows, issueFilter, onlyIssues, search]);

  const visibleEvaluators = useMemo(() => {
    const q = search.trim().toLowerCase();
    return evaluators.filter((evaluator) => {
      if (issueFilter && !evaluator.issues.includes(issueFilter)) return false;
      if (onlyIssues && evaluator.issues.length === 0) return false;
      if (!q) return true;
      return (
        evaluator.name.toLowerCase().includes(q) ||
        evaluator.email.toLowerCase().includes(q)
      );
    });
  }, [evaluators, issueFilter, onlyIssues, search]);

  const total = view === 'application' ? visibleRows.length : visibleEvaluators.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    setPage(1);
  }, [view, issueFilter, onlyIssues, search, limit]);

  const start = (page - 1) * limit;
  const pagedRows = visibleRows.slice(start, start + limit);
  const pagedEvaluators = visibleEvaluators.slice(start, start + limit);

  const coverageBadge = (item: DerivedRow) => {
    if (!item.assignable) {
      return <Badge label="No asignable" variant="danger" />;
    }
    if (item.finalCount === 0) return <Badge label="Sin evaluador" variant="warning" />;
    if (item.finalCount < preview.minEvaluatorsPerApplication) {
      return <Badge label={`${item.finalCount} evaluador`} variant="warning" />;
    }
    return <Badge label={`${item.finalCount} evaluadores`} variant="success" />;
  };

  return (
    <section className={styles.wrapper} aria-label="Previsualización de la asignación">
      <div className={styles.toolbar}>
        <div className={styles.viewToggle} role="group" aria-label="Vista de la tabla">
          <button
            type="button"
            className={view === 'application' ? styles.viewActive : styles.viewButton}
            onClick={() => onViewChange('application')}
          >
            Por postulación
          </button>
          <button
            type="button"
            className={view === 'evaluator' ? styles.viewActive : styles.viewButton}
            onClick={() => onViewChange('evaluator')}
          >
            Por evaluador
          </button>
        </div>

        <div className={styles.searchContainer}>
          <i className="uc-icon icon-size--sm" aria-hidden="true">
            search
          </i>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Buscar por folio, proyecto o evaluador"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className={styles.clearSearch}
              onClick={() => setSearch('')}
              aria-label="Limpiar búsqueda"
            >
              <i className="uc-icon icon-size--sm">close</i>
            </button>
          )}
        </div>

        <Switch
          id="bulk-only-issues"
          label="Solo con incidencias"
          checked={onlyIssues}
          onChange={(e) => setOnlyIssues(e.target.checked)}
        />
      </div>

      {issueFilter && (
        <div className={styles.activeFilter}>
          <span className="uc-p-body-small">
            Filtrando por: <strong>{IMPORT_ISSUE_CATALOG[issueFilter].label}</strong>
          </span>
          <Button
            type="button"
            variant="text"
            size="sm"
            onClick={() => onIssueFilterChange(null)}
          >
            Quitar filtro
          </Button>
        </div>
      )}

      <div className={styles.tableContainer}>
        {total === 0 ? (
          <p className={`uc-p-body ${styles.empty}`}>
            No hay filas que coincidan con el filtro aplicado.
          </p>
        ) : view === 'application' ? (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.checkboxCell}>
                  <span className={styles.srOnly}>Incluir</span>
                </th>
                <th>Folio</th>
                <th>Proyecto / IR</th>
                <th>Evaluadores propuestos</th>
                <th>Tras importar</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((item) => {
                const allDiscarded =
                  item.importableMatches > 0 && item.kept === 0;
                return (
                  <tr key={item.row.key} className={styles.row}>
                    <td className={styles.checkboxCell}>
                      {item.importableMatches > 0 && (
                        <Checkbox
                          id={`row-${item.row.key}`}
                          label={
                            <span className={styles.srOnly}>
                              Incluir la postulación {item.row.folio}
                            </span>
                          }
                          checked={!allDiscarded}
                          onChange={() => onToggleRow(item.row.folio)}
                        />
                      )}
                    </td>
                    <td className={styles.folio}>{item.row.folio}</td>
                    <td>
                      <span className={styles.title}>{item.row.title}</span>
                      <span className={styles.subtle}>
                        {item.row.irName} · {item.row.unidadAcademica}
                      </span>
                      {item.row.issues.map((code) => (
                        <span key={code} className={styles.rowIssue}>
                          <Badge
                            label={IMPORT_ISSUE_CATALOG[code].label}
                            variant={
                              IMPORT_ISSUE_CATALOG[code].severity === 'error'
                                ? 'danger'
                                : 'info'
                            }
                          />
                        </span>
                      ))}
                    </td>
                    <td>
                      {item.row.matches.length === 0 ? (
                        <span className={styles.subtle}>Sin matches en el archivo</span>
                      ) : (
                        <ul className={styles.matches}>
                          {item.row.matches.map((match) => {
                            const isDiscarded = discarded.has(match.key);
                            // El problema de la fila (folio desconocido, estado
                            // no asignable) ya se rotula arriba: repetirlo por
                            // match es ruido.
                            const blocking = match.issues.filter(
                              (code) =>
                                IMPORT_ISSUE_CATALOG[code].severity === 'error' &&
                                !item.row.issues.includes(code)
                            );
                            return (
                              <li
                                key={match.key}
                                className={`${styles.match} ${
                                  !match.importable ? styles.matchBlocked : ''
                                } ${isDiscarded ? styles.matchDiscarded : ''}`}
                              >
                                <span className={styles.matchName}>
                                  {match.evaluatorName}
                                </span>
                                <MatchConfidenceBadge
                                  level={match.confidenceLevel}
                                  value={match.confidence}
                                />
                                {blocking.map((code) => (
                                  <Badge
                                    key={code}
                                    label={IMPORT_ISSUE_CATALOG[code].label}
                                    variant="danger"
                                  />
                                ))}
                                {match.issues.includes('ALREADY_ASSIGNED') && (
                                  <Badge label="Ya asignado" variant="secondary" />
                                )}
                                {match.importable && (
                                  <button
                                    type="button"
                                    className={styles.discardButton}
                                    onClick={() => onToggleMatch(match.key)}
                                    aria-label={
                                      isDiscarded
                                        ? `Restaurar a ${match.evaluatorName}`
                                        : `Descartar a ${match.evaluatorName}`
                                    }
                                    title={isDiscarded ? 'Restaurar' : 'Descartar match'}
                                  >
                                    <i className="uc-icon icon-size--sm">
                                      {isDiscarded ? 'undo' : 'close'}
                                    </i>
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </td>
                    <td>{coverageBadge(item)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Evaluador</th>
                <th>En el archivo</th>
                <th>Carga actual</th>
                <th>Tras importar</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {pagedEvaluators.map((evaluator) => (
                <tr key={evaluator.key} className={styles.row}>
                  <td>
                    <span className={styles.title}>{evaluator.name}</span>
                    <span className={styles.subtle}>{evaluator.email}</span>
                  </td>
                  <td>{evaluator.incoming}</td>
                  <td>{evaluator.currentLoad}</td>
                  <td>
                    {evaluator.projectedLoad} de {preview.quotaPerEvaluator}
                  </td>
                  <td>
                    <div className={styles.statusCell}>
                      {evaluator.issues.length === 0 ? (
                        <Badge label="Dentro del cupo" variant="success" />
                      ) : (
                        evaluator.issues.map((code) => (
                          <Badge
                            key={code}
                            label={IMPORT_ISSUE_CATALOG[code].label}
                            variant={
                              IMPORT_ISSUE_CATALOG[code].severity === 'error'
                                ? 'danger'
                                : IMPORT_ISSUE_CATALOG[code].severity === 'warning'
                                  ? 'warning'
                                  : 'info'
                            }
                          />
                        ))
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > PAGE_SIZES[0] && (
        <Pagination
          pagination={{ page, limit, total, totalPages }}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          pageSizeOptions={PAGE_SIZES}
        />
      )}
    </section>
  );
};

export default ImportPreviewTable;
